import {useState, useEffect, useCallback, useMemo} from 'react';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import useGetRenewalForUser from '@/Hooks/RenewalApi/useGetRenewalForUser';
import {loadChallengeState} from '@/Screens/RenewalPuzzleChallenge/utils';
import {PersistedChallengeState} from '@/Screens/RenewalPuzzleChallenge/types';
import {ScreenName} from '@/navigators/StackConstants';

export type BannerType = 'renewal' | 'global_school' | null;

export interface RenewalBannerData {
  daysLeft?: number;
  sessionsLeft?: number;
  expiryDate?: string;
  couponCode?: string;
  couponDiscountPercentage?: number;
  challengeCompleted: boolean;
  onRenewNow: () => void;
  onClose: () => void;
  onEarnDiscount: () => void;
}

export interface UseTopBannerResult {
  /** Which banner type should currently be displayed */
  activeBanner: BannerType;
  /** Data for the RenewalBanner (only valid when activeBanner === 'renewal') */
  renewalBannerData: RenewalBannerData | null;
  /** Whether the renewal API data is still loading */
  isLoading: boolean;
  /** Dismiss the renewal banner for this session */
  dismissRenewalBanner: () => void;
  /** Raw renewal data from the API */
  renewalData: any;
}

/**
 * Tabs where the RenewalBanner takes precedence and GlobalSchoolBanner is hidden.
 * These tabs already had their own RenewalBanner integration before centralisation.
 */
const RENEWAL_PRIORITY_TABS = ['meeting', 'subscription', 'mentor_calls'];

/**
 * Centralized hook that determines which top-level banner should render
 * inside the "School of Chess" section.
 *
 * Priority rule:
 *   If the user qualifies for a RenewalBanner → show RenewalBanner
 *   Otherwise → show GlobalSchoolBanner
 */
export default function useTopBanner(
  activeTab: string,
  userId?: string | number,
  userContact?: string,
  isCoach?: boolean,
): UseTopBannerResult {
  const navigation = useNavigation();

  // ── Renewal API ──────────────────────────────────────────────
  const {data: renewalData, isLoading: isLoadingRenewal} =
    useGetRenewalForUser();

  // ── Local state ──────────────────────────────────────────────
  const [showRenewalBanner, setShowRenewalBanner] = useState(false);
  const [challengeState, setChallengeState] =
    useState<PersistedChallengeState | null>(null);

  // Evaluate renewal eligibility whenever API data changes
  useEffect(() => {
    if (renewalData?.banner?.should_show) {
      const mobileNumber = renewalData?.user?.mobile || userContact;
      // Show if user has a mobile number and is not a coach
      if (mobileNumber && !isCoach) {
        const daysLeft = Number(
          renewalData?.current_subscription?.days_to_expiry ?? 100,
        );
        // RenewalBanner already hides itself when daysLeft > 7, but we
        // gate here too so we can correctly fall-through to GlobalSchoolBanner.
        setShowRenewalBanner(daysLeft <= 7);
      } else {
        setShowRenewalBanner(false);
      }
      // Read puzzle-challenge state
      const stored = loadChallengeState(userId ? String(userId) : 'guest');
      setChallengeState(stored);
    } else {
      setShowRenewalBanner(false);
    }
  }, [renewalData, userContact, isCoach, userId]);

  // Re-read challenge state when the screen regains focus
  // (user may have just completed the puzzle challenge)
  useFocusEffect(
    useCallback(() => {
      const stored = loadChallengeState(userId ? String(userId) : 'guest');
      setChallengeState(stored);
    }, [userId]),
  );

  // ── Callbacks ────────────────────────────────────────────────
  const dismissRenewalBanner = useCallback(() => {
    setShowRenewalBanner(false);
  }, []);

  const handleRenewNow = useCallback(() => {
    (navigation as any).navigate(ScreenName.Renewal, {
      couponCode: challengeState?.completed
        ? challengeState.couponCode
        : renewalData?.coupon?.code,
    });
  }, [navigation, challengeState, renewalData]);

  const handleEarnDiscount = useCallback(() => {
    (navigation as any).navigate(ScreenName.RenewalPuzzleChallenge);
  }, [navigation]);

  // ── Derived banner data ──────────────────────────────────────
  const renewalBannerData: RenewalBannerData | null = useMemo(() => {
    if (!showRenewalBanner || !renewalData) return null;
    return {
      daysLeft: Number(
        renewalData?.current_subscription?.days_to_expiry ?? 100,
      ),
      sessionsLeft: renewalData?.current_subscription?.sessions_left ?? 0,
      expiryDate: renewalData?.current_subscription?.end_date,
      couponCode: challengeState?.completed
        ? challengeState.couponCode
        : renewalData?.coupon?.code,
      couponDiscountPercentage: challengeState?.completed
        ? challengeState.couponDiscount
        : Number(renewalData?.coupon?.discount_value) ?? 0,
      challengeCompleted: challengeState?.completed ?? false,
      onRenewNow: handleRenewNow,
      onClose: dismissRenewalBanner,
      onEarnDiscount: handleEarnDiscount,
    };
  }, [
    showRenewalBanner,
    renewalData,
    challengeState,
    handleRenewNow,
    dismissRenewalBanner,
    handleEarnDiscount,
  ]);

  // ── Determine active banner ──────────────────────────────────
  const activeBanner: BannerType = useMemo(() => {
    // Only show the renewal banner on specific priority tabs
    if (
      showRenewalBanner &&
      renewalBannerData &&
      RENEWAL_PRIORITY_TABS.includes(activeTab)
    ) {
      return 'renewal';
    }
    // GlobalSchoolBanner shows on all tabs that don't already own a
    // RenewalBanner, OR when the renewal banner is not applicable.
    return 'global_school';
  }, [showRenewalBanner, renewalBannerData, activeTab]);

  return {
    activeBanner,
    renewalBannerData,
    isLoading: isLoadingRenewal,
    dismissRenewalBanner,
    renewalData,
  };
}
